import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  SAVE_REVIEW_POOL_CONTACT_DRAFT,
  SaveReviewPoolContactDraftInput,
  SaveReviewPoolContactDraftResponse,
} from '../graphql/mutations/saveReviewPoolContactDraft';
import { ReviewContactDraftData, fromContactResult } from '../types/reviewPool';

/**
 * 审核池「联系结果确认」的未提交草稿 —— 服务端存（2.1 需求点1，plan §2.5.2）。
 *
 * <h3>为什么需要</h3>
 * 2.1 需求点1 把标准化失败流程改成两步（首次电联 → 二次电联），而现实节奏是
 * 「打第一次电话、上传截图 → 隔一段时间再打第二次」，中间运营必然会切到别的卡片或离开页面。
 * 联系结果与截图若只存在组件 state 里，切条目即清空，整个两步流程实际不可用。
 *
 * <h3>为什么从 localStorage 换成服务端（原实现已推翻）</h3>
 * <ol>
 *   <li><b>恢复出来的截图画不出缩略图</b>：`ImageUploader` 多图模式要求同时给 `displayUrls`
 *       （见其 `fileListFromProps`：无 displayUrls 且 maxCount≠1 时恒返回空列表），而上传后的
 *       预览地址是 `URL.createObjectURL` 的 blob: URL，刷新即失效、存下来是死链。于是界面成了
 *       「截图区空着、下一节点却放开了」——门禁其实按凭证条数正确放行了，但运营会以为门禁失效。</li>
 *   <li><b>localStorage 按浏览器存</b>：隔天做二次电联换人接手 / 换台电脑就拿不到，流程走不完。</li>
 * </ol>
 * 现在草稿落在 `pickup_address_review_pool` 的 4 个列上，随条目一起返回，
 * 截图的可读地址由后端在 GraphQL 类型层预签名给出。
 *
 * <h3>口径</h3>
 * 草稿**按条目共享、不按人**（审核池条目本就是共享待办）。并发下后写覆盖先写，本期接受。
 * 条目处理完成时由后端 `markProcessed` 在同一条 UPDATE 里清空，前端只需重置本地态。
 */
export interface ReviewContactDraft extends ReviewContactDraftData {}

const EMPTY: ReviewContactDraft = {
  proofKeys: [],
  proofUrls: [],
  secondProofKeys: [],
  secondProofUrls: [],
};

/**
 * 回写防抖：运营点单选框、连传多张截图都会连续触发，逐次打接口既浪费也可能乱序。
 * 800ms 足够把一串连续操作并成一次；切条目与组件卸载都会强制 flush，
 * 所以"填完就切走"不会把最后一次输入丢在防抖窗口里。
 */
const SAVE_DEBOUNCE_MS = 800;

/**
 * @param itemId  当前条目 id
 * @param initial 该条目随列表返回的草稿（切条目时作为初值）
 */
export function useReviewContactDraft(itemId?: number, initial?: ReviewContactDraftData) {
  /**
   * 把草稿与它所属的 itemId 绑在同一份 state 里。
   *
   * <p>不是为了好看：拆成两个 state 时，「按 itemId 重置」与「按 draft 回写」两个 effect 会在
   * 同一次 commit 里读到不一致的值，出现「刚载入就把上一条目的草稿写到本条目上」这类串档。
   * 绑定后所有写路径都能先校验 id 再动。
   */
  const [state, setState] = useState<{ id?: number; draft: ReviewContactDraft }>({ draft: EMPTY });
  /** 本次是否带出了已保存的草稿：用于给运营一行提示，否则单选框凭空预选会让人以为是系统选的 */
  const [restored, setRestored] = useState(false);

  const [saveDraft] = useMutation<
    SaveReviewPoolContactDraftResponse,
    { input: SaveReviewPoolContactDraftInput }
  >(
    SAVE_REVIEW_POOL_CONTACT_DRAFT,
    // no-cache：草稿不参与列表渲染，写进缓存只会让同条目的列表项字段被局部覆盖
    { fetchPolicy: 'no-cache' },
  );

  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** 待回写的那一次（含目标条目 id）：切条目/卸载时据此强制 flush */
  const pendingRef = useRef<{ id: number; draft: ReviewContactDraft } | undefined>(undefined);

  const doSave = useCallback(
    (id: number, draft: ReviewContactDraft) => {
      // 四项整体覆盖：后端不做部分更新，所以每次都发当前完整状态，
      // 这样运营"取消勾选/清空截图"才能真的清掉（部分更新会让清空变成不更新）。
      // 失败只吞掉、不弹提示：草稿是过程数据，弹错会打断正在打电话的运营；
      // 真正的提交动作（resolveAs*）有自己的成败提示，那才是不能丢的。
      saveDraft({
        variables: {
          input: {
            reviewPoolId: id,
            contactResult: fromContactResult(draft.contact),
            proofS3Keys: draft.proofKeys,
            secondContactResult: fromContactResult(draft.secondContact),
            secondProofS3Keys: draft.secondProofKeys,
          },
        },
      }).catch(() => undefined);
    },
    [saveDraft],
  );

  const flushPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    const pending = pendingRef.current;
    pendingRef.current = undefined;
    if (pending) doSave(pending.id, pending.draft);
  }, [doSave]);

  // 切换条目：先把上一条目未落地的改动 flush 掉，再用新条目随列表返回的草稿作初值。
  // 初值来自服务端 ⇒ 这里不回写，否则每点开一个条目都会白写一次。
  useEffect(() => {
    flushPending();
    if (itemId == null) {
      setState({ draft: EMPTY });
      setRestored(false);
      return;
    }
    const seeded: ReviewContactDraft = {
      contact: initial?.contact,
      proofKeys: initial?.proofKeys ?? [],
      proofUrls: initial?.proofUrls ?? [],
      secondContact: initial?.secondContact,
      secondProofKeys: initial?.secondProofKeys ?? [],
      secondProofUrls: initial?.secondProofUrls ?? [],
    };
    setState({ id: itemId, draft: seeded });
    setRestored(seeded.contact !== undefined || seeded.proofKeys.length > 0);
    // initial 随列表刷新会拿到新的对象引用，若进依赖数组会把运营正在填的内容覆盖回服务端版本，
    // 所以只按 itemId 重置 —— 同一条目内的服务端更新不应回灌界面。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  // 卸载时 flush（离开页面前把最后一次输入送出去）
  useEffect(() => () => flushPending(), [flushPending]);

  /** 改动草稿：立刻更新界面，防抖回写服务端。 */
  const setDraft = useCallback(
    (updater: (d: ReviewContactDraft) => ReviewContactDraft) => {
      setState((s) => {
        const next = updater(s.draft);
        if (s.id != null) {
          pendingRef.current = { id: s.id, draft: next };
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            timerRef.current = undefined;
            const pending = pendingRef.current;
            pendingRef.current = undefined;
            if (pending) doSave(pending.id, pending.draft);
          }, SAVE_DEBOUNCE_MS);
        }
        return { ...s, draft: next };
      });
    },
    [doSave],
  );

  /**
   * 条目处理完成后调用：只重置本地态，不再发清空请求 —— 后端 markProcessed 已在同一条 UPDATE
   * 里清空了那 4 列。
   *
   * <p>关键是**丢弃待回写的那一次**：否则防抖窗口里的旧草稿会在清空之后被写回去，
   * 条目若"部分处理"重回列表就又带出陈旧凭证。
   */
  const clearDraft = useCallback(
    (id?: number) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      pendingRef.current = undefined;
      setState({ id: id ?? itemId, draft: EMPTY });
      setRestored(false);
    },
    [itemId],
  );

  return { draft: state.draft, setDraft, clearDraft, restored };
}
